﻿module Illy.SafeStack.Client.jQueryUI

open Fable.React
open Fable.React.Props
open Fable.Core.JsInterop
open Elmish.React
open Illy.SafeStack

let private modalOverlay title zIndex closeEvent dispatch width content =
    div [
        ClassName "ui-dialog ui-dialog-safestack ui-corner-all ui-widget ui-widget-content ui-front"
        Style [
            Height "auto"
            ZIndex zIndex
            CSSProp.Position PositionOptions.Static
            Top "auto"
            Left "auto"
        ]
        TabIndex -1
        Role "dialog"
        OnWheel (fun ev -> ev.stopPropagation())
    ] [
        div [
            ClassName "ui-dialog-titlebar ui-corner-all ui-widget-header ui-helper-clearfix"
        ] [
            span [ ClassName "ui-dialog-title" ] [ str title ]
            button [ 
                ClassName "ui-button ui-corner-all ui-widget ui-button-icon-only ui-dialog-titlebar-close" 
                Type "button"
                Title "Close"
                OnClick (fun _ -> dispatch closeEvent)
            ] [
                span [ ClassName "ui-button-icon ui-icon ui-icon-closethick" ] []
                span [ ClassName "ui-button-icon-space" ] []
                str "Close"
            ]
        ]
        div [
            ClassName "carePathNewFormStyle illy-container ui-dialog-content ui-widget-content"
            Style [
                Width "auto"
                MinHeight "0px"
                MaxHeight "90vh"
                ///Required to prevent content from jumping when scrolling is locked by an input with dropdown
                BoxSizing BoxSizingOptions.BorderBox
                Fable.React.Props.CSSProp.Overflow OverflowOptions.Auto
                Width (match width with None -> "auto" | Some x -> sprintf "%dpx" x)
            ]
        ] content
    ]

let modal title zIndexBooster closeEvent dispatch width content = 
    let boostedZ = 100. + float (2*zIndexBooster)
    [
        div [ 
            Class "ui-dialog-container"
            Style [
                CSSProp.Position PositionOptions.Fixed
                Top "0"
                Left "0"
                Width "100vw"
                Height "100vh"
                ZIndex boostedZ
            ]
        ] [
            div [ 
                ClassName "ui-widget-overlay ui-front"
                Style [ 
                    ZIndex (boostedZ + 1.)
                    Width "100%"
                    Height "100%"
                    CSSProp.Position PositionOptions.Absolute
                    Top "0"
                    Left "0"
                ]
            ] []

            div [
                Style [ 
                    ZIndex (boostedZ + 2.)
                    Width "100%"
                    Height "100%"
                    CSSProp.Position PositionOptions.Absolute
                    Top "0"
                    Left "0"
                    Display DisplayOptions.Flex
                    JustifyContent "center"
                    AlignItems AlignItemsOptions.Center
                ]  
            ] [
                modalOverlay title (boostedZ + 3.) closeEvent dispatch width content
            ]            
        ]
    ]

let [<Literal>] animatedBackground = "animated-background"
let [<Literal>] hideText = "hide-text"
let [<Literal>] fixedWidth = "fixed-width"

let skeletonScreen = sprintf "%s %s" animatedBackground hideText

///append "animated-background", "hide-text" & any optional CSS classes to existing ones while loading
let addSkeletonScreen existingClasses classesWhileLoading loading=
    if loading then
        match classesWhileLoading with
        | Some c -> 
            sprintf "%s %s %s" existingClasses skeletonScreen c
        | None -> 
            sprintf "%s %s" existingClasses skeletonScreen
    else 
        existingClasses

///append "hide-text" & any optional CSS classes to existing ones while loading
let hideTextIfLoading existingClasses classesWhileLoading loading=
    if loading then
        match classesWhileLoading with
        | Some c -> 
            sprintf "%s %s %s" existingClasses hideText c
        | None -> 
            sprintf "%s %s" existingClasses hideText
    else 
        existingClasses

/// Display 22x22px skeleton screen box
let skeletonScreenIcon = 
    span [
        ClassName skeletonScreen
        Style [
            Height "22px"
            Width "22px"
            Display DisplayOptions.InlineBlock
        ]
    ] []

let private labelInternal id lbl validation validationWarning loading mandatory disabled =
    let classes = 
        sprintf 
            "control-label%s%s"
            (if mandatory then " mandatory" else "")
            (if disabled then " disabled" else "")
    div [ ClassName "label" ] [
        yield label [ 
            ClassName (addSkeletonScreen classes None loading)
            HtmlFor id ] [ str lbl ]
        match validation with 
         | Some s -> 
            yield span [ ClassName "validation ui-state-error" ] [ str s ]
         | None -> ()
        if validation.IsNone then 
            match validationWarning with 
            | Some s -> 
            yield span [ ClassName "validation ui-state-highlight" ] [ str s ]
            | None -> ()
    ]

let labelWithMandatory id lbl validation validationWarning loading mandatory =
    labelInternal id lbl validation validationWarning loading mandatory false

let labelWithDisabled id lbl validation validationWarning loading disabled =
    labelInternal id lbl validation validationWarning loading false disabled
    
let labelWithMandatoryAndDisabled id lbl validation validationWarning loading mandatory disabled =
    labelInternal id lbl validation validationWarning loading mandatory disabled
    
let label id lbl validation validationWarning loading = 
    labelInternal id lbl validation validationWarning loading false false

let inputGroupWithClasses additionalClasses =
    let classes = "input-group"::additionalClasses |> String.concat " "
    div [ ClassName classes ]

let inputGroup = inputGroupWithClasses []

let readOnlyField id lbl text loading=
    inputGroup [
        label id lbl None None loading
        span [ 
            Id id 
            ClassName (addSkeletonScreen "" (Some fixedWidth) loading)
        ] [ str text ]
    ]

let selectInput id placeholder onchange dispatch (options: ListType<'a>) selected loading =
    let trySendChange str =
        if str = "" then
            dispatch (onchange None)
        else
            let id = System.Int32.Parse str
            
            let found = ListOption.getListItemById id options
            dispatch (onchange <| Some found)
    select [ 
            Id id 
            ClassName (addSkeletonScreen "form-control" None loading)
            OnChange (fun ev -> trySendChange !!ev.target?value)
            Value (selected |> Option.map (ListOption.getId >> (fun x -> x.ToString())) |> Option.defaultValue "" )
        ] [
            match placeholder with
            | None -> ()
            | Some pl -> yield option [ Value ""; Selected(match selected with None -> true | _ -> false) ] [ str pl ]
            let options = ListOption.convertToList options

            let groups = options |> List.groupBy (fun x -> x.Group)
            let (ungrouped, grouped) = groups |> List.partition (fun x -> fst x = "")
            for (g, opts) in grouped do
                yield optgroup [
                    Label g
                ] [
                    for opt in opts do
                        yield option [ 
                            Value (opt.Id.ToString()); 
                            Selected (match selected with None -> false | Some s -> opt.Id = s.Id) 
                        ] [ str opt.Value ] 
                ]
            if ungrouped.Length > 0 then
                let options = [
                    for opt in snd(ungrouped.[0]) do
                        yield option [ 
                            Value (opt.Id.ToString()); 
                            Selected (match selected with None -> false | Some s -> opt.Id = s.Id) 
                        ] [ str opt.Value ]
                ]
                if grouped.Length = 0 then
                    for opt in options do yield opt
                else
                    yield optgroup [ Label "Ungrouped" ] options
        ]

let select id lbl validation validationWarning placeholder onchange dispatch (options: ListType<'a>) selected loading=    
    inputGroup [
        div [ ClassName "label" ] [
            label id lbl validation validationWarning loading
        ]
        selectInput id placeholder onchange dispatch (options: ListType<'a>) selected loading  
    ]

let numberPicker id lbl validation validationWarning placeholder onchange dispatch min max selected loading=
     inputGroup [
        div [ ClassName "label" ] [
            label id lbl validation validationWarning loading
        ]
        input [ 
            Id id 
            Type "number"
            ClassName (addSkeletonScreen "form-control" None loading)
            OnChange (fun ev -> dispatch (onchange !!ev.target?value))
            Placeholder (match placeholder with Some pl when not loading -> pl | _ -> "")
            Min min
            Max max
            Helpers.valueOrDefault (match selected with None -> "" | Some v -> v.ToString())
            Disabled loading
        ]
     ]

/// Text input by itself
let textInput id placeholder onchange dispatch max selected loading=
    input [ 
        Id id 
        ClassName (addSkeletonScreen "form-control" None loading)
        Type "text"
        OnChange (fun ev -> dispatch (onchange !!ev.target?value))
        Placeholder (match placeholder with Some pl when not loading -> pl | _ -> "" )
        MaxLength max
        Helpers.valueOrDefault (match selected with None -> "" | Some v -> v)
        Disabled loading
    ] 

/// Text input with a label 
let labelledTextInput id lbl validation validationWarning placeholder onchange dispatch max selected loading mandatory =
    [
        labelWithMandatory id lbl validation validationWarning loading mandatory
        textInput id placeholder onchange dispatch max selected loading
    ]

/// Text input in an input group with a label
let textWithMandatory id lbl validation validationWarning placeholder onchange dispatch max selected loading mandatory =
    inputGroup <| labelledTextInput id lbl validation validationWarning placeholder onchange dispatch max selected loading mandatory

let textWithClasses id lbl validation validationWarning placeholder onchange dispatch max selected loading mandatory classes =
    inputGroupWithClasses classes <| labelledTextInput id lbl validation validationWarning placeholder onchange dispatch max selected loading mandatory

let text id lbl validation validationWarning placeholder onchange dispatch max selected loading = textWithMandatory id lbl validation validationWarning placeholder onchange dispatch max selected loading false

///checkbox by itself
let checkInput id onchange dispatch selected loading = 
    if loading then
        skeletonScreenIcon
    else
        input [ 
            Id id 
            ClassName (addSkeletonScreen "form-control" None loading)
            Type "checkbox"
            OnChange (fun ev -> dispatch (onchange !!ev.target?``checked``))
            Checked selected
        ]

///checkbox inside an input group
let check id lbl validation validationWarning onchange dispatch selected loading=
     inputGroup [
        yield label id lbl validation validationWarning loading

        if loading then
            yield skeletonScreenIcon
        else
            yield checkInput id onchange dispatch selected loading
     ]

let checkInline id lbl validation validationWarning onchange dispatch selected loading =
    inputGroupWithClasses ["checkbox"] [
        if loading then
            yield skeletonScreenIcon
        else
            yield checkInput id onchange dispatch selected loading
            yield label id lbl validation validationWarning loading
    ]
    

type DraggableDirection =
| Horizontal
| Vertical
| Both
| Neither

let textareaWithMandatory id lbl validation validationWarning placeholder onchange dispatch max selected loading isMandatory=
     inputGroup [
        div [ ClassName "label" ] [
            labelWithMandatory id lbl validation validationWarning loading isMandatory
        ]
        textarea [ 
            Id id 
            ClassName (addSkeletonScreen "form-control" None loading)
            OnChange (fun ev -> dispatch (onchange !!ev.target?value))
            Placeholder (match placeholder with Some pl when not loading -> pl | _ -> "")
            MaxLength max
            Helpers.valueOrDefault (match selected with None -> "" | Some v -> v)
            Rows 4
            Disabled loading
        ] []
    ]

let textareaWithMandatoryAndDragRestriction id lbl validation validationWarning placeholder onchange dispatch max selected (draggableDirection: DraggableDirection) loading isMandatory =
    let directionStyle = 
        function
        | Horizontal -> "horizontal"
        | Vertical -> "vertical"
        | Both -> "both"
        | Neither -> "none"

    inputGroup [
       div [ ClassName "label" ] [
           labelWithMandatory id lbl validation validationWarning loading isMandatory
       ]
       textarea [ 
           Id id 
           ClassName (addSkeletonScreen "form-control" None loading)
           OnChange (fun ev -> dispatch (onchange !!ev.target?value))
           Placeholder (match placeholder with Some pl when not loading -> pl | _ -> "")
           MaxLength max
           Helpers.valueOrDefault (match selected with None -> "" | Some v -> v)
           Rows 4
           Disabled loading
           Style [ Resize (directionStyle draggableDirection) ] 
        ] []
    ]

let textarea id lbl validation validationWarning placeholder onchange dispatch max selected loading = 
    textareaWithMandatoryAndDragRestriction id lbl validation validationWarning placeholder onchange dispatch max selected DraggableDirection.Vertical loading false
    
open FontAwesome

let actionWithDisabled lbl icon onClick dispatch disabled =
    div [ 
        ClassName (sprintf "action %s" (if disabled then "disabled" else ""))
        OnClick 
            (fun _ -> 
                if disabled then ()
                else dispatch onClick
            ) 
    ] [
        solid icon [Fixed] []
        str lbl
    ]

let action lbl icon onClick dispatch = actionWithDisabled lbl icon onClick dispatch false

///loading spinner - fa-5x
let spinner = light "circle-notch loading" [Animate Spin; Size X5] []

///saving spinner - fa-2x
let saveSpinner = light "circle-notch" [Animate Spin; Size X2] []

let buttonWithDisabled disabled className text onclick dispatch =
    button [
        Type "button"
        ClassName (sprintf "ui-button ui-corner-all ui-widget%s%s"
            (match className with None -> "" | Some x -> " " + x)
            (match disabled with false -> "" | true -> " ui-state-disabled"))
        OnClick (fun ev -> dispatch onclick)
        Disabled disabled
    ] [ str text ]
    
let buttonWithClass className text onclick dispatch = buttonWithDisabled false className text onclick dispatch
let button text onclick dispatch = buttonWithClass None text onclick dispatch

let toggleButtonsContainer list = div [ ClassName "toggleButtons" ] list

///single toggle button which should be wrapped in toggleButtonsContainer - CSS classes are defined in toggleButton.less
let toggleButton (icon: string option) title (description: string) isSelected isDisabled dispatch onClick loading =

    let hasDescription = not <| System.String.IsNullOrWhiteSpace description
    let buttonClasses = 
        sprintf "toggleButton %s %s %s"
            (if isSelected then "selected" else "")
            (if isDisabled then "disabled" else "")
            (if not hasDescription then "toggleButton-textCentered" else "")

    div [   
        ClassName (addSkeletonScreen buttonClasses None loading)
        OnClick (fun _ -> if loading || isDisabled then () else dispatch onClick)
    ] [
        if not loading then
            match icon with Some str -> yield regular str [Fixed] [] | None -> ()

        yield div [ 
                ClassName "buttonText" 
            ] [
                yield h3 [ ClassName <| sprintf "title %s" (if loading then hideText else "") ] [ str title ]
                
                if hasDescription then
                    yield p [ ClassName <| sprintf "description %s" (if loading then hideText else "") ] [ str description ]
            ]
    ]

let illy_info x = div [ ClassName "illy-info" ] x

let filesAndDialogues rootPath episodeId itemId itemType fileCount dialogueCount prependString =
    let prependString' = prependString |> Option.map (sprintf "&prependCaseNoteString=%s") |> Option.defaultValue ""
    let addNoItemsClass itemsCount = if itemsCount > 0 then "" else "noItems"
    div [
        ClassName "attachedItemContainer"
    ] [
        span [
            ClassName "filesDialogues linkSize"
        ] [
            a [
                Href <| sprintf "%s/FileAttachment/ViewClientItemFileAttachment.castle?episodeId=%d&itemId=%d&itemType=%s%s" rootPath episodeId itemId itemType prependString'
                Target "__blank"
                Title <| sprintf "There %s %d file attachment%s" (if fileCount = 1 then "is" else "are") fileCount (if fileCount = 1 then "" else "s")
            ] [
                i [ 
                    ClassName <| sprintf "far fa-paperclip %s" (addNoItemsClass fileCount)
                ] []
            ]
            a [
                Href <| sprintf "%s/CaseNote/ViewClientItemCaseNotes.castle?episodeId=%d&itemId=%d&itemType=%s%s" rootPath episodeId itemId itemType prependString'
                Target "__blank"
                Title <| sprintf "There %s %d casenote%s" (if dialogueCount = 1 then "is" else "are") dialogueCount (if dialogueCount = 1 then "" else "s")
            ] [
                i [ 
                    ClassName <| sprintf "fa fa-align-justify %s" (addNoItemsClass dialogueCount)
                ] []
            ]
        ]
    ]

/// Time by itself
let rawTime formatter (id: string) (onchange: ParsedTime * string option -> 'wrap) (dispatch: 'wrap -> unit) selected loading=
    input [ 
        Id id 
        ClassName (addSkeletonScreen "timepicker" None loading)
        HTMLAttr.Type "text"
        OnInput (fun ev -> dispatch ((ParsedTime.ofString >> onchange) !!ev.target?value))
        MaxLength 5.
        Helpers.valueOrDefault <| match ParsedTime.formatToOption formatter selected with None -> "" | Some v -> v
        Disabled loading
    ]

/// Time with label
let labelledTime formatter (id: string) lbl (onchange: ParsedTime * string option -> 'wrap) (dispatch: 'wrap -> unit) selected validationError validationWarning loading=
    [    
        div [ ClassName "label" ] [
            yield label id lbl validationError validationWarning loading
        ]
        
        rawTime formatter id onchange dispatch selected loading
    ]

/// Time with input group and label
let time formatter (id: string) lbl (onchange: ParsedTime * string option -> 'wrap) (dispatch: 'wrap -> unit) selected validationError validationWarning loading= 
    inputGroup <| labelledTime formatter id lbl onchange dispatch selected validationError validationWarning loading

let validationErrors content =
    div [ ClassName "validationErrors" ] content


let illyTable headers noDataMsg data mapper =
    match data with
    | [] -> illy_info [ span [] [ str noDataMsg ] ]
    | _ ->
        table [ ClassName "illy-grid" ] [
            thead [] [
                tr [] [
                    for header in headers do
                        match header with
                        | Some h -> yield th [] [ str h]
                        | None -> yield th [] []
                ]
            ]
            tbody [] [
                for row in data do
                    yield tr [] [
                        let cols = mapper row
                        for col in cols do
                            yield td [] col
                    ]
            ]
        ]

let saveControls saveButtonName gettingData saving saveClickedMsg cancelMsg dispatch = 
    [
        if not gettingData then
            yield div [ ClassName "saveControls"]
                [
                    if not saving then
                        yield button (FSharp.Core.Option.defaultValue "Save" saveButtonName) saveClickedMsg dispatch
                    else 
                        yield saveSpinner

                    yield span [] [str "or"]
                    yield a [ OnClick (fun _ -> dispatch cancelMsg) ] [str "Cancel" ]
                ]
    ]

/// display the view function of a model if some
let conditionallyRenderView overlayModel viewFunc overlayMsg dispatch = 
    [
        match overlayModel with
        | Some overlayModel -> yield! viewFunc overlayModel (overlayMsg >> dispatch)
        | None -> () 
    ]

type ColumnMetaData<'a> = 
    {
        Header: (ReactElement list) option
        CellGetter: 'a -> ReactElement list
    }

module ColumnMetaData =
    let create header cellGetter =
        {
            Header = header
            CellGetter = cellGetter
        }
        
    let createWithHeader header cellGetter =
        create (Some header) cellGetter

    let createWithoutHeader cellGetter =
        create None cellGetter

let (===>) = ColumnMetaData.create

let simpleTableWithRowAttributes<'a> rowAttributeGetter noItemsMessage (columns: ColumnMetaData<'a> list) (loading: bool) (rows: 'a list) = 
    if List.isEmpty rows && not loading then
        illy_info [ str noItemsMessage] 
                
    else
        let headers = List.map (fun x -> th [] [ match x.Header with |Some h -> yield! h | None -> () ] ) columns
                
        let buildRow row = 
            columns
            |> List.map (fun x -> td [] (x.CellGetter row) )
            |> tr (rowAttributeGetter row)

        let emptyRow = columns |> List.map (fun _ -> td [] [ str "\a0" ]) |> tr []

        table [ ClassName (if loading then "animated-background hide-text" else "") ]
            [
                thead [] [
                    tr [] headers
                ] 
                        
                tbody [] 
                    (
                        if loading then 
                            [ for i in 0..3 do yield emptyRow ] 
                        else 
                            List.map buildRow rows
                    )
            ]

let simpleTable noItemsMessage columns loading rows = simpleTableWithRowAttributes (fun _ -> []) noItemsMessage columns loading rows

let versionCheckFailedOverlay entityName closeEvent dispatch = 
    modal "Another user has edited this item" 8000 closeEvent dispatch (Some 800)
        [
            illy_info  
                [ 
                    str (sprintf "Another user has edited this %s while you were updating it. Your changes have not been saved. The form will be updated with the most up to date version of the %s" entityName entityName) 
                ]
            div [ ClassName "saveControls"] [
                    a [ OnClick (fun _ -> dispatch closeEvent)] [ str "Close" ] 
                ]
        ]
        
let itemWasDeletedOverlay entityName closeEvent dispatch = 
    modal "Another user has deleted this item" 8000 closeEvent dispatch (Some 800)
        [
            illy_info  
                [ 
                    str (sprintf "Another user has deleted this %s while you were updating it. Your changes have not been saved" entityName) 
                ]

            div [ ClassName "saveControls"] [
                    a [ OnClick (fun _ -> dispatch closeEvent)] [ str "Close" ] 
                ]
        ]
